import React from 'react';

import {
  ILearningCenterArticleFields,
  ILearningCenterSidebarCallout,
  IVariation,
  IVariationContainer
} from '@/types/contentful';

import { ArticleHeader } from '../Components/ArticleHeader';
import { BlockContainer } from '../../Layout';
import RichText from '../../RichText';
import { StickySidebar } from '../Components/StickySidebar';
import { VariationContainer } from '../../VariationContainer';
import { SocialShare } from '../Components/ArticleHeader/SocialShare';
import classNames from 'classnames';
import { MobileStickySignup } from '../Components/MobileStickySignup';
import { usePageDataContext } from '~/context/PageData';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import {
  EmbeddedInlineEntryNode,
  ParagraphNode
} from '~/components/RichText/Nodes';
import { useOptimizely } from '~/context/Optimizely';

interface ArticleProps extends ILearningCenterArticleFields {
  sysId?: string;
}

// Utility function to render either a VariationContainer or the default component
function renderWithFallback(
  globalContent: any,
  DefaultComponent: any,
  extraProps = {}
) {
  if (globalContent) {
    return globalContent.sys.contentType.sys.id === 'variationContainer' ? (
      <VariationContainer
        key={globalContent.sys.id}
        sysId={globalContent.sys.id}
        {...globalContent.fields}
        {...extraProps}
      />
    ) : (
      <DefaultComponent {...globalContent.fields} {...extraProps} />
    );
  }
  // If no global content, render the default component with the passed props
  return null;
}

export function Article({
  body,
  difficulty,
  heroImage,
  publishDate,
  seoMetadata,
  sidebar,
  summary,
  sysId,
  tagsList,
  title,
  category,
  mobileSignup
}: ArticleProps) {
  const experiments = useOptimizely();
  const newsletterExpKey = 'newsletter_sign-up_shift_xp_5_6';
  const currentNewsletterVariationKey =
    experiments?.[newsletterExpKey]?.variationKey;

  const RICH_TEXT_OVERRIDE: RenderNode = {
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode
        node={node}
        colour="inlineAnchor"
        fontWeight={'500'}
      >
        {children}
      </EmbeddedInlineEntryNode>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <ParagraphNode node={node} className="[&_p]:text-base">
        {children}
      </ParagraphNode>
    )
  };

  const {
    articleConfig: {
      globalNewsletterSignupMobile,
      globalArticleBannerCta,
      bannerCtaArticleDepth,
      globalRelatedContent,
      globalNewsletterSignupSidebar
    } = {}
  } = usePageDataContext();

  // Build the props for RichText based on the presence of globalArticleBannerCta
  const richTextProps = globalArticleBannerCta
    ? {
        data: body,
        isArticle: true,
        globalBanner: globalArticleBannerCta,
        bannerDepth: bannerCtaArticleDepth
      }
    : {
        data: body
      };

  const isCalloutFeatured =
    (!!sidebar &&
      !!sidebar.find(
        (block) =>
          block.sys.contentType.sys.id === 'learningCenterSidebarCallout' &&
          (block as ILearningCenterSidebarCallout)?.fields.isFeatured
      )) ||
    (!!globalNewsletterSignupSidebar &&
      (globalNewsletterSignupSidebar.sys.contentType.sys.id !==
      'variationContainer'
        ? (globalNewsletterSignupSidebar as ILearningCenterSidebarCallout)
            ?.fields.isFeatured
        : Array.isArray(
            (globalNewsletterSignupSidebar as IVariationContainer).fields
              .variations
          ) &&
          (
            globalNewsletterSignupSidebar as IVariationContainer
          ).fields.variations?.some(
            (item: IVariation) =>
              item.fields.pageBlock?.sys.contentType.sys.id ===
                'learningCenterSidebarCallout' &&
              item.fields?.variationKey === currentNewsletterVariationKey &&
              (item.fields.pageBlock as ILearningCenterSidebarCallout)?.fields
                ?.isFeatured
          )));

  return (
    <article>
      <ArticleHeader
        title={title}
        thumbnail={heroImage}
        category={category?.fields.name}
        tagsList={tagsList}
        sysId={sysId}
        summary={summary}
        difficulty={difficulty}
        publishDate={publishDate}
        seoFields={seoMetadata?.fields}
      />
      <BlockContainer
        marginTop={{ 0: 'm', tablet: 'ml' }}
        marginBottom={{ 0: 'ml', tablet: 'l', desktop: 'xl' }}
        data-testid="blog-post-body"
      >
        <div className="-m-3 flex flex-shrink-0 flex-grow basis-full flex-col flex-wrap justify-center tablet:-m-2 desktop:-m-6 desktop:flex-row [&>*]:p-4 tablet:[&>*]:p-2 desktop:[&>*]:p-6">
          {(sidebar || globalNewsletterSignupSidebar || globalRelatedContent) &&
            isCalloutFeatured && (
              <div
                className={classNames(
                  'z-[1] block w-full shrink-0 grow-0 desktop:hidden desktop:w-1/3',
                  isCalloutFeatured ? 'mb-6' : ''
                )}
              >
                <StickySidebar
                  blocks={
                    isCalloutFeatured
                      ? sidebar?.filter(
                          (element: any) =>
                            element.sys.contentType.sys.id ===
                            'learningCenterSidebarCallout'
                        )
                      : sidebar
                  }
                />
              </div>
            )}
          <div
            className={classNames(
              'z-[1] w-full desktop:mt-0',
              sidebar || globalNewsletterSignupSidebar || globalRelatedContent
                ? 'desktop:w-8/12'
                : 'desktop:w-11/12',
              'shrink-0 grow-0'
            )}
          >
            <div className="">
              {/* Render RichText with conditional props */}
              <RichText
                {...richTextProps}
                overrideOptions={RICH_TEXT_OVERRIDE}
              />
              <hr className="mb-4 border-t-2 border-dotted" />
              <SocialShare
                {...seoMetadata?.fields}
                socialOptions={'all'}
                inline={false}
              />
            </div>
          </div>
          {(sidebar ||
            globalNewsletterSignupSidebar ||
            globalRelatedContent) && (
            <div
              className={classNames(
                'z-[1] w-full shrink-0 grow-0 desktop:w-1/3'
              )}
            >
              <StickySidebar
                blocks={
                  isCalloutFeatured
                    ? sidebar?.filter(
                        (element: any) =>
                          element.sys.contentType.sys.id !==
                          'learningCenterSidebarCallout'
                      )
                    : sidebar
                }
                excludeSidebarCallout={isCalloutFeatured}
              />
            </div>
          )}
          {/* Render article-specific MobileStickySignup first, fallback to global if not available */}
          {mobileSignup ? (
            <div className="min:w-1/3 z-10 w-full shrink-0 grow-0 desktop:hidden">
              <MobileStickySignup {...mobileSignup?.fields} />
            </div>
          ) : (
            renderWithFallback(globalNewsletterSignupMobile, MobileStickySignup)
          )}
        </div>
      </BlockContainer>
    </article>
  );
}
